import React, { useEffect, useMemo, useState } from 'react';
import { Animated, Easing, Platform, StyleSheet, View, type StyleProp, type ViewStyle } from 'react-native';
import { useAppTheme } from '@/theme';

const SPOKES = 12;
/** What 'small' and 'large' mean, the same sizes as the system spinner this replaces. */
const SIZES = { small: 20, large: 36 } as const;

export interface SpinnerProps {
  /** 'small' (20) or 'large' (36), or any size in points. */
  size?: 'small' | 'large' | number;
  /** The spokes' colour; defaults to the theme's accent. */
  color?: string;
  /** Pass false to hide it, like the system spinner it replaces. */
  animating?: boolean;
  style?: StyleProp<ViewStyle>;
  accessibilityLabel?: string;
}

/**
 * The app's one loading spinner: twelve spokes that fade round in a circle, turning in twelve
 * steps like the iPhone spinner. Every loading state in the app uses it, in buttons, on pages and
 * over a dimmed screen, so loading looks the same everywhere. Drawn with plain views, so it works
 * the same on Android, iOS and the website.
 */
export function Spinner({ size = 'small', color, animating = true, style, accessibilityLabel }: SpinnerProps) {
  const { colors } = useAppTheme();
  const tint = color ?? colors.accent;
  const box = typeof size === 'number' ? size : SIZES[size];
  const [turn] = useState(() => new Animated.Value(0));

  useEffect(() => {
    if (!animating) return;
    turn.setValue(0);
    const loop = Animated.loop(
      Animated.timing(turn, { toValue: 1, duration: 1000, easing: Easing.linear, useNativeDriver: Platform.OS !== 'web' }),
    );
    loop.start();
    return () => loop.stop();
  }, [animating, turn]);

  // A whole turn in 12 equal jumps: the fade pattern steps one spoke at a time rather than gliding.
  const rotate = useMemo(() => {
    const inputRange: number[] = [];
    const outputRange: string[] = [];
    for (let i = 0; i < SPOKES; i++) {
      inputRange.push(i / SPOKES, (i + 0.999) / SPOKES);
      outputRange.push(`${i * 30}deg`, `${i * 30}deg`);
    }
    inputRange.push(1);
    outputRange.push('360deg');
    return turn.interpolate({ inputRange, outputRange });
  }, [turn]);

  if (!animating) return null;

  const spokeWidth = Math.max(1.5, box * 0.085);
  const spokeLength = box * 0.26;
  const distance = box * 0.37;

  return (
    <View
      accessible
      accessibilityRole="progressbar"
      accessibilityLabel={accessibilityLabel ?? 'Loading'}
      style={[{ width: box, height: box }, style]}>
      <Animated.View style={[StyleSheet.absoluteFill, { transform: [{ rotate }] }]}>
        {Array.from({ length: SPOKES }, (_, i) => (
          <View
            key={i}
            style={{
              position: 'absolute',
              left: box / 2 - spokeWidth / 2,
              top: box / 2 - spokeLength / 2,
              width: spokeWidth,
              height: spokeLength,
              borderRadius: spokeWidth / 2,
              backgroundColor: tint,
              opacity: 0.14 + 0.86 * ((i + 1) / SPOKES),
              transform: [{ rotate: `${i * 30}deg` }, { translateY: -distance }],
            }}
          />
        ))}
      </Animated.View>
    </View>
  );
}
